import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaFilter, FaRupeeSign } from "react-icons/fa";
import {
  FiEye,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
  FiDownload,
} from "react-icons/fi";
import Select from "react-select";

import {
  useGetSalesReturnGstReportQuery,
  useDownloadSalesReturnGstReportMutation,
} from "../toolkitServices/invoiceApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";

const fmt2 = (n) => Number(n || 0).toFixed(2);
const money = (n) => `₹ ${fmt2(n)}`;
const dIN = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
};

const THEME = {
  primary: "#6b59c6",
  ring: "#7c3aed",
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 12,
    borderColor: state.isFocused ? THEME.ring : "#e5e7eb",
    boxShadow: state.isFocused ? `${THEME.ring}22 0 0 0 3px` : "none",
    "&:hover": { borderColor: THEME.ring },
  }),
  menu: (base) => ({ ...base, zIndex: 70 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function TableSkeletonRows() {
  return Array.from({ length: 8 }).map((_, i) => (
    <tr key={i} className="border-t">
      {Array.from({ length: 12 }).map((__, j) => (
        <td key={j} className="px-3 py-3">
          <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  ));
}

function CompactSummaryBar({ summary }) {
  const items = [
    { label: "Returns", value: summary?.salesReturnCount || 0 },
    { label: "Taxable", value: money(summary?.taxableTotal || 0) },
    { label: "CGST", value: money(summary?.cgstTotal || 0) },
    { label: "SGST", value: money(summary?.sgstTotal || 0) },
    { label: "IGST", value: money(summary?.igstTotal || 0) },
    { label: "Total", value: money(summary?.grandTotal || 0), highlight: true },
  ];

  return (
    <div className="shrink-0 border-b bg-gradient-to-r from-slate-50 via-violet-50/60 to-slate-50 px-2.5 py-2 sm:px-4 sm:py-2.5">
      <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item) => (
          <div
            key={item.label}
            className={`min-w-[112px] shrink-0 rounded-xl border px-3 py-2 shadow-sm ${
              item.highlight
                ? "border-violet-500 bg-gradient-to-r from-[#6b59c6] to-[#806dcc] text-white"
                : "border-violet-100 bg-white text-[#4b3d91]"
            }`}
          >
            <div
              className={`text-[9px] font-semibold uppercase tracking-wide ${
                item.highlight ? "text-white/80" : "text-slate-500"
              }`}
            >
              {item.label}
            </div>
            <div className="mt-0.5 truncate text-[11px] font-bold tabular-nums sm:text-sm">
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GstReturnDetailsModal({ open, onClose, salesReturn }) {
  const itemDetails = salesReturn?.itemDetails || [];

  const taxable = salesReturn?.taxable || 0;
  const cgst = salesReturn?.cgst || 0;
  const sgst = salesReturn?.sgst || 0;
  const igst = salesReturn?.igst || 0;
  const gst = salesReturn?.gst || 0;
  const total = salesReturn?.total || 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/45" />

          <motion.div
            className="relative w-full max-w-[900px] bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden"
            initial={{ y: 16, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 16, scale: 0.98, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 sm:px-5 py-3 text-white bg-gradient-to-r from-violet-500 to-indigo-600">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm sm:text-base font-extrabold">
                    {salesReturn?.srCode || "-"}
                  </div>
                  <div className="text-[11px] text-white/85 mt-1">
                    Invoice: <b>{salesReturn?.invoiceCode || "-"}</b> • Date:{" "}
                    <b>{dIN(salesReturn?.srDate)}</b> • Refund:{" "}
                    <b>{salesReturn?.refundMode || "-"}</b>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="p-2 rounded-full bg-white/15 hover:bg-white/25"
                >
                  <FiX />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-5">
              <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-6">
                <div className="rounded-xl border bg-[#f7f4ff] px-3 py-2">
                  <div className="text-[11px] text-slate-500">Taxable</div>
                  <div className="text-sm font-extrabold text-[#4b3d91]">{money(taxable)}</div>
                </div>
                <div className="rounded-xl border bg-[#f7f4ff] px-3 py-2">
                  <div className="text-[11px] text-slate-500">CGST</div>
                  <div className="text-sm font-extrabold text-[#4b3d91]">{money(cgst)}</div>
                </div>
                <div className="rounded-xl border bg-[#f7f4ff] px-3 py-2">
                  <div className="text-[11px] text-slate-500">SGST</div>
                  <div className="text-sm font-extrabold text-[#4b3d91]">{money(sgst)}</div>
                </div>
                <div className="rounded-xl border bg-[#f7f4ff] px-3 py-2">
                  <div className="text-[11px] text-slate-500">IGST</div>
                  <div className="text-sm font-extrabold text-[#4b3d91]">
                    {money(igst)}
                  </div>
                </div>
                <div className="rounded-xl border bg-[#f7f4ff] px-3 py-2">
                  <div className="text-[11px] text-slate-500">GST</div>
                  <div className="text-sm font-extrabold text-[#4b3d91]">{money(gst)}</div>
                </div>
                <div className="rounded-xl border bg-white px-3 py-2">
                  <div className="text-[11px] text-slate-500">Total</div>
                  <div className="text-sm font-extrabold text-slate-900">{money(total)}</div>
                </div>
              </div>

              <div className="rounded-2xl border overflow-hidden">
                <div className="px-4 py-2.5 flex justify-between bg-[#efeafd]">
                  <div className="text-sm font-extrabold text-[#4b3d91]">
                    Item Details
                  </div>
                  <span className="text-[11px] font-semibold text-[#4b3d91] bg-white px-2 py-0.5 rounded-full">
                    {itemDetails.length} item(s)
                  </span>
                </div>

                <div className="max-h-[48vh] overflow-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="sticky top-0 bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Item</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Net Wt</th>
                        <th className="px-3 py-2 text-right">Taxable</th>
                        <th className="px-3 py-2 text-right">CGST</th>
                        <th className="px-3 py-2 text-right">SGST</th>
                        <th className="px-3 py-2 text-right">IGST</th>
                        <th className="px-3 py-2 text-right">GST</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {itemDetails.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="py-8 text-center text-slate-500">
                            No item details found
                          </td>
                        </tr>
                      ) : (
                        itemDetails.map((it, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 font-semibold text-slate-900">
                              <div>{it?.itemName || "-"}</div>
                              <div className="text-[10px] text-slate-500">
                                {it?.categoryName || "-"} / {it?.designName || "-"} /{" "}
                                {it?.purityName || "-"}
                              </div>
                            </td>
                            <td className="px-3 py-2">{it?.itemType || "-"}</td>
                            <td className="px-3 py-2 text-right">{fmt2(it?.qty)}</td>
                            <td className="px-3 py-2 text-right">{fmt2(it?.netWt)}</td>
                            <td className="px-3 py-2 text-right">{money(it?.taxable)}</td>
                            <td className="px-3 py-2 text-right">{money(it?.cgst)}</td>
                            <td className="px-3 py-2 text-right">{money(it?.sgst)}</td>
                            <td className="px-3 py-2 text-right">{money(it?.igst)}</td>
                            <td className="px-3 py-2 text-right">{money(it?.gst)}</td>
                            <td className="px-3 py-2 text-right font-bold text-[#4b3d91]">
                              {money(it?.total)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={onClose}
                  className="rounded-full text-white font-bold text-sm px-5 py-2"
                  style={{ backgroundColor: THEME.primary }}
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function GstReturnReport() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [filters, setFilters] = useState({
    branchId: "",
    fromDate: "",
    toDate: "",
  });

  const [draftFilters, setDraftFilters] = useState({
    branchId: "",
    fromDate: "",
    toDate: "",
  });

  const [srPage, setSrPage] = useState(1);
  const [srLimit, setSrLimit] = useState(10);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);

  const [downloadSalesReturnGstReport, { isLoading: isDownloading }] =
    useDownloadSalesReturnGstReportMutation();

  const queryParams = useMemo(
    () => ({
      ...filters,
      srPage,
      srLimit,
      itemPage: 1,
      itemLimit: 5000,
    }),
    [filters, srPage, srLimit]
  );

  const { data, isFetching, isLoading, isError, error } =
    useGetSalesReturnGstReportQuery(queryParams, {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    });

  const { data: branches, isFetching: branchLoading } = useGetAllBranchesQuery();

  const branchOptions = useMemo(
    () =>
      branches?.data?.map((b) => ({
        label: b.branchName,
        value: b._id,
      })) || [],
    [branches]
  );

  const branchMap = useMemo(() => {
    const m = {};
    (branches?.data || []).forEach((b) => {
      m[String(b._id)] = b.branchName;
    });
    return m;
  }, [branches]);

  const summary = data?.summary || {};
  const invoiceWiseItems = useMemo(
    () => data?.invoiceWiseItems || [],
    [data],
  );

  const pagination = data?.pagination?.srSummary || data?.srSummaryPagination || {};

  const rows = useMemo(() => {
    return invoiceWiseItems.map((sr) => ({
      ...sr,
      _id: sr?.srId,
      branchName: branchMap[String(sr?.branchId)] || "-",
      itemCount: sr?.itemDetails?.length || 0,
    }));
  }, [invoiceWiseItems, branchMap]);

  const currentPage = Number(pagination?.page || srPage || 1);
  const currentLimit = Number(pagination?.limit || srLimit || 10);
  const totalRecords = Number(pagination?.totalRecords || 0);
  const totalPages = Number(pagination?.totalPages || 1);
  const hasNextPage = Boolean(pagination?.hasNextPage);
  const hasPrevPage = Boolean(pagination?.hasPrevPage);

  const showingFrom = totalRecords === 0 ? 0 : (currentPage - 1) * currentLimit + 1;
  const showingTo = Math.min(currentPage * currentLimit, totalRecords);

  const hasInvalidDateRange = Boolean(
    draftFilters.fromDate &&
      draftFilters.toDate &&
      draftFilters.fromDate > draftFilters.toDate
  );

  const openFilterModal = () => {
    setDraftFilters(filters);
    setIsFilterOpen(true);
  };

  const clearAppliedFilters = () => {
    setFilters({ branchId: "", fromDate: "", toDate: "" });
    setSrPage(1);
  };

  const openDetails = (sr) => {
    setSelectedReturn(sr);
    setDetailOpen(true);
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadSalesReturnGstReport({
        branchId: filters.branchId,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales-return-gst-report-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Sales Return GST report download failed:", err);
    }
  };

  return (
    <div className="flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-[#6b59c6] via-[#806dcc] to-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaRupeeSign className="text-sm" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              GST Return Report
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">
              Invoice-wise sales return GST summary
            </p>
          </div>
          {isFetching && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <FiLoader className="animate-spin" /> refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 sm:inline-flex">
            {totalRecords} returns
          </span>
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-xl bg-white text-emerald-700 shadow-sm transition hover:bg-emerald-50 disabled:opacity-60 sm:w-auto sm:rounded-full sm:px-3"
              title="Download Excel"
              aria-label="Download Excel"
            >
              {isDownloading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}
              <span className="hidden text-[11px] font-semibold sm:inline">
                {isDownloading ? "Downloading…" : "Download"}
              </span>
            </button>

            <button
              type="button"
              onClick={openFilterModal}
              className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 sm:w-auto sm:rounded-full sm:px-3"
              title="Filters"
              aria-label="Open filters"
            >
              <FaFilter className="text-xs" />
              <span className="hidden text-[11px] font-semibold sm:inline">
                Filters
              </span>
            </button>
        </div>
      </div>

      {/* Active filters */}
      {(filters.branchId || filters.fromDate || filters.toDate) && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {filters.branchId && (
              <FilterChip
                label={`Branch: ${branchMap[filters.branchId] || "Selected"}`}
              />
            )}
            {filters.fromDate && (
              <FilterChip label={`From: ${dIN(filters.fromDate)}`} />
            )}
            {filters.toDate && (
              <FilterChip label={`To: ${dIN(filters.toDate)}`} />
            )}
          </div>
          <button
            type="button"
            onClick={clearAppliedFilters}
            className="shrink-0 text-[10px] font-semibold text-[#6b59c6] hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {!isLoading && !isFetching && rows.length > 0 && (
        <CompactSummaryBar summary={summary} />
      )}

      {/* Returns */}
      <div className="min-h-0 flex-1 overflow-auto bg-slate-50/70 p-2.5 sm:p-4">
        <div className="h-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[780px] text-xs sm:min-w-full sm:text-sm">
          <thead className="sticky top-0 z-10 bg-[#efeafd] text-[10px] uppercase tracking-wide text-[#4b3d91] sm:text-[11px]">
            <tr>
              <th className="px-3 py-2.5 text-left">SR No</th>
              <th className="px-3 py-2.5 text-left">Invoice</th>
              <th className="px-3 py-2.5 text-left">Date</th>
              <th className="hidden px-3 py-2.5 text-left sm:table-cell">
                Branch
              </th>
              <th className="px-3 py-2.5 text-right">Items</th>
              <th className="px-3 py-2.5 text-right">Taxable</th>
              <th className="hidden border-l-2 border-l-violet-200 bg-violet-50/60 px-3 py-2.5 text-right md:table-cell">
                CGST
              </th>
              <th className="hidden border-l border-l-violet-100 bg-violet-50/40 px-3 py-2.5 text-right md:table-cell">
                SGST
              </th>
              <th className="hidden border-l border-l-violet-100 bg-violet-50/40 px-3 py-2.5 text-right md:table-cell">
                IGST
              </th>
              <th className="hidden border-l border-l-violet-100 bg-violet-50/40 px-3 py-2.5 text-right lg:table-cell">
                GST
              </th>
              <th className="border-l-2 border-l-violet-300 bg-violet-100/70 px-3 py-2.5 text-right">
                Total
              </th>
              <th className="px-3 py-2.5 text-center">View</th>
            </tr>
          </thead>

          <tbody className="bg-white text-slate-700">
            {(isLoading || isFetching) && <TableSkeletonRows />}

            {!isLoading && !isFetching && isError && (
              <tr>
                <td colSpan={12} className="py-10 text-center text-rose-600">
                  {error?.data?.message || "Unable to load GST return report"}
                </td>
              </tr>
            )}

            {!isLoading && !isFetching && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={12} className="py-10 text-center text-slate-500">
                  No records found
                </td>
              </tr>
            )}

            {!isLoading &&
              !isFetching &&
              !isError &&
              rows.map((sr) => (
                <tr
                  key={sr.srId}
                  className="border-t border-slate-100 transition hover:bg-violet-50/40"
                >
                  <td className="px-3 py-3 font-semibold text-slate-900">
                    {sr.srCode}
                    <div className="sm:hidden text-[10px] text-slate-500">
                      {sr.branchName}
                    </div>
                  </td>

                  <td className="px-3 py-3 font-medium">{sr.invoiceCode || "-"}</td>

                  <td className="px-3 py-3 whitespace-nowrap">{dIN(sr.srDate)}</td>

                  <td className="hidden sm:table-cell px-3 py-3">
                    {sr.branchName}
                  </td>

                  <td className="px-3 py-3 text-right font-bold">
                    {sr.itemCount}
                  </td>

                  <td className="px-3 py-3 text-right font-semibold whitespace-nowrap">
                    {money(sr.taxable)}
                  </td>

                  <td className="hidden whitespace-nowrap border-l-2 border-l-violet-100 bg-violet-50/20 px-3 py-3 text-right md:table-cell">
                    {money(sr.cgst)}
                  </td>

                  <td className="hidden whitespace-nowrap border-l border-l-violet-100 bg-violet-50/20 px-3 py-3 text-right md:table-cell">
                    {money(sr.sgst)}
                  </td>

                  <td className="hidden whitespace-nowrap border-l border-l-violet-100 bg-violet-50/20 px-3 py-3 text-right md:table-cell">
                    {money(sr.igst)}
                  </td>

                  <td className="hidden whitespace-nowrap border-l border-l-violet-100 bg-violet-50/20 px-3 py-3 text-right font-semibold lg:table-cell">
                    {money(sr.gst)}
                  </td>

                  <td className="whitespace-nowrap border-l-2 border-l-violet-200 bg-violet-50/50 px-3 py-3 text-right font-bold tabular-nums text-[#4b3d91]">
                    {money(sr.total)}
                  </td>

                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => openDetails(sr)}
                      className="inline-flex items-center justify-center gap-1 rounded-full bg-[#6b59c6] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#5948ae]"
                    >
                      <FiEye />
                      <span className="hidden sm:inline">View</span>
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        </div>
      </div>

      {!isLoading && !isFetching && rows.length > 0 && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-t bg-white px-2 py-1.5 text-[10px] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
          <div className="hidden text-slate-600 sm:block">
            Showing <span className="font-semibold">{showingFrom}</span>–
            <span className="font-semibold">{showingTo}</span> of{" "}
            <span className="font-semibold">{totalRecords}</span>
          </div>

          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
            <div className="flex shrink-0 items-center gap-1">
              <span className="hidden text-slate-500 sm:inline">Rows:</span>
              <Select
                value={{ value: srLimit, label: String(srLimit) }}
                options={[10, 25, 50, 100].map((number) => ({
                  value: number,
                  label: String(number),
                }))}
                onChange={(option) => {
                  setSrLimit(Number(option?.value) || 10);
                  setSrPage(1);
                }}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[54px]"
                aria-label="Rows per page"
              />
              <span className="text-[9px] text-slate-400 sm:hidden">
                / page
              </span>
            </div>

            <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none">
              <button
                type="button"
                onClick={() => setSrPage((page) => Math.max(page - 1, 1))}
                disabled={!hasPrevPage}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
                aria-label="Previous page"
              >
                <FiChevronLeft />
                <span className="hidden sm:inline">Prev</span>
              </button>

              <span className="text-slate-600">
                <span className="flex items-center gap-1 sm:hidden">
                  <Select
                    value={{ value: currentPage, label: String(currentPage) }}
                    options={Array.from(
                      { length: Math.max(1, totalPages) },
                      (_, index) => ({
                        value: index + 1,
                        label: String(index + 1),
                      }),
                    )}
                    onChange={(option) =>
                      setSrPage(Number(option?.value) || 1)
                    }
                    isSearchable={false}
                    menuPlacement="top"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    menuShouldScrollIntoView={false}
                    styles={paginationSelectStyles}
                    className="w-[52px]"
                    aria-label="Select page number"
                  />
                  <span>/ {Math.max(1, totalPages)}</span>
                </span>
                <span className="hidden sm:inline">
                  Page <span className="font-semibold">{currentPage}</span> of{" "}
                  <span className="font-semibold">
                    {Math.max(1, totalPages)}
                  </span>
                </span>
              </span>

              <button
                type="button"
                onClick={() =>
                  setSrPage((page) =>
                    Math.min(page + 1, Math.max(1, totalPages)),
                  )
                }
                disabled={!hasNextPage}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
                aria-label="Next page"
              >
                <span className="hidden sm:inline">Next</span>
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-[#6b59c6] to-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">
                    GST Return Filters
                  </h3>
                  <p className="mt-0.5 text-[10px] text-violet-100">
                    Filter by branch and date range
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  aria-label="Close filters"
                >
                  <FiX />
                </button>
              </div>

              <div className="space-y-3 p-4">
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-slate-600">
                    Branch
                  </label>
                  <Select
                    styles={selectStyles}
                    options={branchOptions}
                    isLoading={branchLoading}
                    menuPortalTarget={document.body}
                    value={
                      draftFilters.branchId
                        ? branchOptions.find(
                            (x) => x.value === draftFilters.branchId
                          ) || null
                        : null
                    }
                    onChange={(o) =>
                      setDraftFilters((s) => ({
                        ...s,
                        branchId: o?.value || "",
                      }))
                    }
                    placeholder="Select branch"
                    isClearable
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <div className="mb-1 text-[10px] font-medium text-slate-600">
                      From date
                    </div>
                    <input
                      type="date"
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                      value={draftFilters.fromDate}
                      onChange={(e) =>
                        setDraftFilters((s) => ({
                          ...s,
                          fromDate: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <div className="mb-1 text-[10px] font-medium text-slate-600">
                      To date
                    </div>
                    <input
                      type="date"
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                      value={draftFilters.toDate}
                      onChange={(e) =>
                        setDraftFilters((s) => ({
                          ...s,
                          toDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                {hasInvalidDateRange && (
                  <p className="text-[10px] font-medium text-rose-600">
                    The From date must be earlier than or equal to the To date.
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() =>
                    setDraftFilters({ branchId: "", fromDate: "", toDate: "" })
                  }
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-700"
                >
                  Reset
                </button>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-semibold text-slate-600"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (hasInvalidDateRange) return;
                      setFilters(draftFilters);
                      setSrPage(1);
                      setIsFilterOpen(false);
                    }}
                    disabled={hasInvalidDateRange}
                    className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <GstReturnDetailsModal
        open={detailOpen}
        salesReturn={selectedReturn}
        onClose={() => {
          setDetailOpen(false);
          setSelectedReturn(null);
        }}
      />
    </div>
  );
}

function FilterChip({ label }) {
  return (
    <span className="shrink-0 rounded-full border border-violet-100 bg-white px-2.5 py-1 text-[10px] font-medium text-slate-600">
      {label}
    </span>
  );
}
